import React, { useState, useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { useDineCentralAuth } from '../../hooks/useDineCentralAuth';
import { useTheme } from '../../hooks/ThemeContext';
import { useRestaurant } from '../../hooks/RestaurantContext';
import { HiMail, HiLogout, HiGift } from 'react-icons/hi';
import Spinner from '../../components/Spinner/Spinner';
import { DINECENTRAL_AUTH } from '../../Firebase.dinecentral';
import { getRemoteEnv } from '../../config/remoteEnv';
import { fetchLoyaltyPublicConfig } from '../../service/LoyaltyService';

// Account — one responsive column: profile, email preferences (toggle saves
// immediately), sign out. Styled like the Rewards page (accent-derived
// tones on the brand colour, cards on the page background).

const css = `
.ac-page { min-height: 100%; overflow-y: auto; box-sizing: border-box; -webkit-font-smoothing: antialiased; text-align: left; }
.ac-shell { max-width: 640px; margin: 0 auto; padding: 26px 16px 110px; box-sizing: border-box; }
@media (min-width: 900px) { .ac-shell { padding: 42px 28px 120px; } }
.ac-title { font-size: 26px; font-weight: 800; letter-spacing: -0.4px; margin: 0 0 4px; }
.ac-sub { opacity: .62; font-size: 14px; margin: 0 0 22px; line-height: 1.5; }
@media (min-width: 900px) { .ac-title { font-size: 32px; } }

.ac-card {
  background: var(--ac-card); border: 1px solid var(--ac-border); border-radius: 18px;
  padding: 20px 22px; margin-bottom: 14px;
}
.ac-card-title { font-size: 11px; font-weight: 700; letter-spacing: .1em; opacity: .55; margin-bottom: 16px; }

.ac-profile { display: flex; align-items: center; gap: 16px; }
.ac-avatar {
  width: 54px; height: 54px; border-radius: 50%; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  font-size: 22px; font-weight: 800; color: #fff;
  background: linear-gradient(135deg, var(--ac-accent) 0%, var(--ac-accent-deep) 100%);
}
.ac-profile-name { font-weight: 700; font-size: 16.5px; line-height: 1.3; }
.ac-profile-email { font-size: 13.5px; opacity: .6; margin-top: 2px; word-break: break-all; }

.ac-row { display: flex; align-items: center; gap: 14px; }
.ac-row-text { flex: 1; min-width: 0; }
.ac-row-title { font-weight: 700; font-size: 14.5px; }
.ac-row-sub { font-size: 12.5px; opacity: .6; line-height: 1.45; margin-top: 3px; }
.ac-status { font-size: 12px; font-weight: 600; margin-top: 10px; opacity: .65; }
.ac-status.err { color: #e5484d; opacity: 1; }

.ac-switch {
  position: relative; width: 50px; height: 28px; border-radius: 999px; flex-shrink: 0;
  border: none; cursor: pointer; padding: 0;
  background: var(--ac-border); transition: background .18s ease;
}
.ac-switch.checked { background: var(--ac-accent); }
.ac-switch::after {
  content: ""; position: absolute; top: 3px; left: 3px; width: 22px; height: 22px;
  border-radius: 50%; background: #fff; box-shadow: 0 1px 3px rgba(0,0,0,.25);
  transition: transform .18s ease;
}
.ac-switch.checked::after { transform: translateX(22px); }
.ac-switch:disabled { opacity: .55; cursor: default; }

.ac-link {
  display: flex; align-items: center; gap: 12px; width: 100%; text-align: left;
  background: none; border: none; cursor: pointer; font: inherit; color: inherit; padding: 0;
}
.ac-link svg { color: var(--ac-accent); font-size: 20px; flex-shrink: 0; }
.ac-link .ac-row-sub { margin-top: 2px; }
.ac-chev { opacity: .4; margin-left: auto; }

.ac-signout {
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  width: 100%; padding: 13px 20px; border-radius: 12px; cursor: pointer; font-family: inherit;
  font-size: 14px; font-weight: 700; border: 1px solid rgba(229,72,77,.35);
  color: #e5484d; background: rgba(229,72,77,.06); transition: background .15s ease;
}
.ac-signout:hover { background: rgba(229,72,77,.12); }
`;

const hexToRgb = (hex: string): [number, number, number] | null => {
  const m = hex.replace('#', '');
  const full = m.length === 3 ? m.split('').map((c) => c + c).join('') : m;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return null;
  return [parseInt(full.slice(0, 2), 16), parseInt(full.slice(2, 4), 16), parseInt(full.slice(4, 6), 16)];
};

const AccountPage: React.FC = () => {
  const auth = useDineCentralAuth();
  const { theme } = useTheme();
  const { restaurantId } = useRestaurant();
  const navigate = useNavigate();

  const [marketingEnabled, setMarketingEnabled] = useState<boolean>(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<'' | 'saved' | 'error'>('');
  const [loyaltyOn, setLoyaltyOn] = useState(false);

  useEffect(() => {
    setMarketingEnabled(auth.agreeToMarketing === true);
  }, [auth.agreeToMarketing]);

  // The Rewards link only makes sense where rewards exist (same gate as the
  // sidebar tab): loyalty on, store has rewards, not a menu-only site.
  useEffect(() => {
    if (!restaurantId || getRemoteEnv().menuOnly) return;
    let live = true;
    fetchLoyaltyPublicConfig(restaurantId)
      .then((c) => live && setLoyaltyOn(c.enabled && c.rewards.length > 0))
      .catch(() => {});
    return () => { live = false; };
  }, [restaurantId]);

  const handleToggle = async () => {
    if (saving) return;
    const next = !marketingEnabled;
    setMarketingEnabled(next);
    setSaving(true);
    setStatus('');
    try {
      await auth.updateMarketingPreference(next);
      setStatus('saved');
    } catch {
      setMarketingEnabled(!next);
      setStatus('error');
    } finally {
      setSaving(false);
    }
  };

  const handleSignOut = async () => {
    try { await signOut(DINECENTRAL_AUTH); } catch { /* signed out locally regardless */ }
    navigate('/');
  };

  if (auth.loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', backgroundColor: theme.backgroundColor }}>
        <Spinner />
      </div>
    );
  }

  if (!auth.user) {
    const redirectUrl = encodeURIComponent(window.location.pathname + window.location.search);
    return <Navigate to={`/login?redirect=${redirectUrl}`} />;
  }

  const accent = getRemoteEnv().themeOverrideAccentColor || theme.payButtonColor || '#4169E1';
  const rgb = hexToRgb(accent) || [65, 105, 225];
  const vars = {
    '--ac-accent': accent,
    '--ac-accent-deep': `rgb(${Math.round(rgb[0] * 0.62)}, ${Math.round(rgb[1] * 0.62)}, ${Math.round(rgb[2] * 0.62)})`,
    '--ac-card': theme.cardColor,
    '--ac-border': theme.borderColor,
  } as React.CSSProperties;

  const displayName = auth.user.displayName || '';
  const email = auth.user.email || '';
  const initial = (displayName || email || '?').trim().charAt(0).toUpperCase();

  return (
    <div className="ac-page" style={{ backgroundColor: theme.backgroundColor, color: theme.textColor, ...vars }}>
      <style>{css}</style>
      <div className="ac-shell">
        <h1 className="ac-title">Account</h1>
        <p className="ac-sub">Your profile and preferences for this restaurant.</p>

        <div className="ac-card">
          <div className="ac-card-title">PROFILE</div>
          <div className="ac-profile">
            <span className="ac-avatar">{initial}</span>
            <div style={{ minWidth: 0 }}>
              <div className="ac-profile-name">{displayName || 'Welcome back'}</div>
              <div className="ac-profile-email">{email}</div>
            </div>
          </div>
        </div>

        <div className="ac-card">
          <div className="ac-card-title">EMAIL PREFERENCES</div>
          <div className="ac-row">
            <HiMail style={{ color: 'var(--ac-accent)', fontSize: 20, flexShrink: 0 }} />
            <div className="ac-row-text">
              <div className="ac-row-title">Offers &amp; seasonal menus</div>
              <div className="ac-row-sub">Occasional emails with promotions and what's new — unsubscribe anytime.</div>
            </div>
            <button
              className={`ac-switch ${marketingEnabled ? 'checked' : ''}`}
              onClick={handleToggle}
              disabled={saving}
              aria-checked={marketingEnabled}
              role="switch"
              aria-label="Marketing emails"
            />
          </div>
          {status === 'saved' && <div className="ac-status">Preference saved.</div>}
          {status === 'error' && <div className="ac-status err">Couldn't save — please try again.</div>}
        </div>

        {loyaltyOn && (
          <div className="ac-card">
            <button className="ac-link" onClick={() => navigate('/rewards')}>
              <HiGift />
              <div className="ac-row-text">
                <div className="ac-row-title">Your rewards</div>
                <div className="ac-row-sub">Check your points and spend them on the good stuff.</div>
              </div>
              <span className="ac-chev">›</span>
            </button>
          </div>
        )}

        <button className="ac-signout" onClick={handleSignOut}>
          <HiLogout /> Sign out
        </button>
      </div>
    </div>
  );
};

export default AccountPage;
